import { ActionKind } from '@dynamic/ui';
import { FC } from 'react';
import { useLocation } from 'react-router';
import styled, { css } from 'styled-components';

import { useActions } from '#/hooks/actions/useActions';
import { useConfig } from '#/hooks/useConfig';
import { CoreFooterFeatureItem } from '#/service/config/features.types';
import { ButtonVariant } from '#/service/constants';
import { InternalPageId } from '#/service/constants/menu';
import { remify, techSearchParams } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { MenuButton, MenuLink, MenuNavLink } from '../Header/components';
import {
  getNavigationTarget,
  getNavigationTargetId,
} from '../Header/Menu.utils';

const internalActionViews = [InternalPageId.Login, InternalPageId.Iap];

export const FooterLink: FC<CoreFooterFeatureItem> = ({ display, action }) => {
  const { handleAction, actions } = useActions();
  const { search: locationSearch } = useLocation();
  const { pages } = useConfig();

  if (
    !action.kind ||
    // ItemAction is handled entirely on the DynUi side, so ActionDictionary doesn't include a handler for it, so we have to exclude here
    action.kind === ActionKind.ItemAction ||
    !actions[action.kind]
  ) {
    console.warn('FooterLink: Unsupported action', action);

    return null;
  }

  const onClick = () => {
    handleAction(action);
  };

  const search = techSearchParams(locationSearch);
  const title = tb(display.title);

  const commonProps = {
    key: getNavigationTargetId({ action }),
    'aria-label': title,
    children: (
      <span className="title" data-testid="footerItemTitle" key="title">
        {title}
      </span>
    ),
  };

  const { internalUrl, externalUrl } = getNavigationTarget(
    { action },
    '',
    pages
  );

  if (action.kind === ActionKind.NavigateExternal) {
    return (
      <FooterExternalLink
        {...commonProps}
        href={externalUrl}
        rel="noreferrer"
        role="footeritem"
        target="_blank"
      />
    );
  }

  if (
    (action.kind === ActionKind.NavigateInternal ||
      action.kind === ActionKind.NavigatePage) &&
    !internalActionViews.includes(action.parameters?.view as InternalPageId)
  ) {
    return (
      <FooterInternalLink
        {...commonProps}
        role="footeritem"
        to={{ pathname: internalUrl, search }}
      />
    );
  }

  return (
    <FooterButton
      $buttonVariant={ButtonVariant.Ghost}
      onClick={onClick}
      role="footeritem"
      type="button"
      {...commonProps}
    />
  );
};

FooterLink.displayName = 'FooterLink';

const footerLinkStyle = css`
  margin: ${remify(6)} 0;
  padding: 0;
  height: auto;
  border-bottom: none;
  border-top: none;

  &&& {
    color: var(--pageParagraphForeground);
  }
`;

const FooterButton = styled(MenuButton)`
  ${footerLinkStyle}

  &&& {
    padding: 0;
  }
`;

const FooterExternalLink = styled(MenuLink)`
  ${footerLinkStyle}
`;

const FooterInternalLink = styled(MenuNavLink)`
  ${footerLinkStyle}
`;
